import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { History, Link as LinkIcon, PhoneCall, UserCog } from "lucide-react";

/** Right column: related leads + full history panel — extracted from LeadDetail.jsx. */
export default function LeadHistoryPanel({ lead, related }) {
  const navigate = useNavigate();
  // Old/New tag is derived from where the number sits NOW (active vs retired by client refresh).
  const refreshed = (lead.retired_numbers || []).length > 0;
  const retiredSet = new Set((lead.retired_numbers || []).map((n) => n.e164));
  const activeSet = new Set((lead.numbers || []).map((n) => n.e164));
  const numberTag = (phone) => {
    if (!refreshed || !phone) return null;
    if (retiredSet.has(phone)) return { label: "Old number", cls: "bg-slate-100 text-slate-600 border-slate-200" };
    if (activeSet.has(phone)) return { label: "New number", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" };
    return null;
  };
  return (
      <aside className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-indigo-600" />
              Related Leads · {related.length}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {related.length === 0 ? (
              <div className="text-[11px] text-slate-400 p-2">No related leads.</div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {related.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate(`/leads/${r.id}`)}
                    className="w-full text-left px-2 py-1.5 rounded border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 text-xs transition-colors"
                    data-testid={`related-${r.customer_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">{r.customer_name}</span>
                      <Badge variant="outline" className="font-mono text-[9px]">{r.customer_id}</Badge>
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500 font-mono truncate">
                      {r.matching_numbers.join(", ")}
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-400 flex gap-2">
                      <span>HQ: {r.hq_available ? "Yes" : "No"}</span>
                      {r.disposition && <span>· Disp: {r.disposition}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 text-[11px]">
            <div className="mb-2 flex items-center justify-between text-slate-500">
              <span>Total dials{refreshed && <span className="text-[9px] text-slate-400"> · old + new numbers</span>}</span>
              <span className="font-mono text-slate-800" data-testid="total-dials">
                {Math.max((lead.call_history || []).filter((h) => h.phone).length, lead.attempts || 0)}
              </span>
            </div>
            <Separator className="my-2" />
            <div className="font-semibold text-slate-600 mb-1">Field Edits</div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {(lead.field_edit_history || []).slice(-30).reverse().map((h, i) => (
                <div key={i} className="px-1.5 py-1 rounded bg-slate-50 border border-slate-100">
                  <div className="font-mono text-[10px] text-slate-700 truncate">{h.field}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {String(h.old ?? "—")} → <span className="text-slate-800">{String(h.new ?? "—")}</span>
                  </div>
                  <div className="text-[9px] text-slate-400 flex justify-between">
                    <span>{h.by_name || h.by || "system"}</span>
                    <span>{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                  </div>
                </div>
              ))}
              {(lead.field_edit_history || []).length === 0 && (
                <div className="text-slate-400">No edits yet.</div>
              )}
            </div>
            <Separator className="my-2" />
            <div className="font-semibold text-slate-600 mb-1">Assignment History</div>
            <div className="space-y-1 max-h-40 overflow-auto" data-testid="assignment-history">
              {(lead.assignment_history || []).slice(-20).reverse().map((h, i) => (
                <div key={i} className="px-1.5 py-1 rounded bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <UserCog className="w-3 h-3 text-slate-500 shrink-0" />
                    <span className="text-[10px] text-slate-700 truncate">
                      {h.from ? `${h.from} → ` : ""}{h.assigned_to || "unassigned"}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{h.reason || "—"}</div>
                  <div className="text-[9px] text-slate-400 flex justify-between">
                    <span>{h.by || "system"}</span>
                    <span>{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                  </div>
                </div>
              ))}
              {(lead.assignment_history || []).length === 0 && (
                <div className="text-slate-400">No assignments yet.</div>
              )}
            </div>
            <Separator className="my-2" />
            <div className="font-semibold text-slate-600 mb-1">Call History</div>
            <div className="space-y-1 max-h-40 overflow-auto">
              {(lead.call_history || []).slice(-30).reverse().map((h, i) => (
                <div key={i} className="px-1.5 py-1 rounded bg-slate-50 border border-slate-100"
                     data-testid={`call-history-entry-${i}`}>
                  <div className="flex items-center gap-1.5">
                    <PhoneCall className="w-3 h-3 text-slate-500" />
                    <span className="font-mono text-[10px] text-slate-700 truncate">{h.phone || h.kind || h.event_type || "no number"}</span>
                    {numberTag(h.phone) && (
                      <Badge variant="outline" className={`text-[8px] h-4 px-1 ${numberTag(h.phone).cls}`}
                             data-testid={`call-history-numtag-${i}`}>
                        {numberTag(h.phone).label}
                      </Badge>
                    )}
                    <Badge variant="outline" className="ml-auto text-[9px] font-mono"
                           data-testid={`call-history-outcome-${i}`}>
                      {h.genesys_result || h.outcome || h.event_type || "—"}
                    </Badge>
                  </div>
                  {h.genesys_result && h.outcome && h.outcome !== h.genesys_result && (
                    <div className="text-[9px] text-slate-400 mt-0.5" data-testid={`call-history-mapping-${i}`}>
                      Kippu mapping: {h.outcome}
                      {h.disposition_code ? ` (${h.disposition_code})` : ""} — suggestion only, the
                      agent&apos;s disposition is unchanged
                    </div>
                  )}
                  {h.wrapup && (
                    <div className="text-[9px] text-slate-400">Wrap-up: {h.wrapup}</div>
                  )}
                  <div className="text-[9px] text-slate-400 flex justify-between mt-0.5">
                    <span>{h.by_name || h.by || h.via || "system"}</span>
                    <span>{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                  </div>
                </div>
              ))}
              {(lead.call_history || []).length === 0 && (
                <div className="text-slate-400">No calls yet.</div>
              )}
            </div>
            <Separator className="my-2" />
            <div className="font-semibold text-slate-600 mb-1">Remarks</div>
            <div className="space-y-1 max-h-36 overflow-auto">
              {(lead.remarks_history || []).slice(-20).reverse().map((h, i) => (
                <div key={i} className="px-1.5 py-1 rounded bg-slate-50 border border-slate-100">
                  <div className="text-slate-800">{h.text}</div>
                  <div className="text-[9px] text-slate-400 flex justify-between">
                    <span>{h.by_name || h.by || "system"}</span>
                    <span>{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                  </div>
                </div>
              ))}
              {(lead.remarks_history || []).length === 0 && (
                <div className="text-slate-400">No remarks yet.</div>
              )}
            </div>
          </CardContent>
        </Card>
      </aside>
  );
}
